import { useCallback, useEffect, useMemo, useState } from "react";

import debounce from "lodash.debounce";

import { useConfig } from "@jti/app-context/useConfig";
import { toast } from "@jti/core-components/components/Toaster";
import type { FlotiqStore } from "@jti/fe-common";
import { FlotiqApi } from "@jti/rest-api";
import { useTranslations } from "@jti/ui/providers/useTranslations";

import type { StoreLocatorFilter, StoreLocatorPosition, StoreLocatorSelectedFilter } from "../../types";
import {
  getFiltersBaseOnFlotiqResponse,
  getLatLngAndRadius,
  getNewFiltersBaseOnFlotiqResponse,
  getStoresFilteredByProductAttributes,
  getStoresFilteredBySelectedFilters,
  getTransformedMarkers,
} from "../../utils";
import { getInitialDataFromURL } from "../../utils/convertSharedFilterData";
import { useFiltersShare } from "../useFiltersShare";

import type { GetFlotiqStores, MapPosition, UseFlotiqStores } from "./useFlotiqStores.types";

const DEFAULT_NUMBER_OF_STORES = 1000;
type NoStoresFiltersState = { value: boolean; type: "filters" };

export const useFlotiqStores: UseFlotiqStores = (params) => {
  const {
    cartItems,
    filterByCartAvailabilityEnabled,
    filtersTranslations,
    storesLimit = DEFAULT_NUMBER_OF_STORES,
    activeStore,
    activeStoreId: activeStoreIdFromParams,
    flotiqApiKey,
    flotiqContentType,
    enabledFiltersCategories,
    withinCategoryFilterOperator,
    isFiltersSharingEnabled,
    categoryAttributeFilter,
    isAsianAddressFormatEnabled,
  } = params || {};
  const { __ } = useTranslations();
  const { FLOTIQ_API_URL, FLOTIQ_API_KEY, STORE_LOCATOR_CONTENT_TYPE } = useConfig();

  const [stores, setStores] = useState<FlotiqStore[]>([]);
  const [activeStoreId, setActiveStoreId] = useState<string | undefined>(activeStoreIdFromParams);
  const [filteredStores, setFilteredStores] = useState<FlotiqStore[]>([]);
  const [userMarkerPosition, setUserMarkerPosition] = useState<StoreLocatorPosition>();
  const [isNoMatchingFlotiqStores, setIsNoMatchingFlotiqStores] = useState<boolean>(false);

  const [filters, setFilters] = useState<StoreLocatorFilter>([]);
  const [newFilters, setNewFilters] = useState<StoreLocatorFilter>([]);
  const [isFiltersOpened, setIsFiltersOpened] = useState(false);

  const initialFilters = useMemo(() => getInitialDataFromURL(isFiltersSharingEnabled), []);

  const [selectedFilters, setSelectedFilters] = useState<Record<string, StoreLocatorSelectedFilter>>(
    initialFilters.selectedFilters ?? {}
  );
  const [mapPosition, setMapPosition] = useState<MapPosition | undefined>(
    initialFilters.mapPosition && Object.keys(initialFilters.mapPosition).length
      ? initialFilters.mapPosition
      : undefined
  );
  useFiltersShare({ mapPosition, selectedFilters, isFiltersSharingEnabled });
  const [isNoStoresFilters, setIsNoStoresFilters] = useState<NoStoresFiltersState | null>(
    Object.keys(initialFilters.selectedFilters ?? {}).length ? { value: true, type: "filters" } : null
  );

  const flotiqApi = useMemo(
    () =>
      new FlotiqApi(FLOTIQ_API_URL, flotiqApiKey || FLOTIQ_API_KEY, flotiqContentType || STORE_LOCATOR_CONTENT_TYPE),
    [FLOTIQ_API_KEY, FLOTIQ_API_URL, STORE_LOCATOR_CONTENT_TYPE, flotiqApiKey, flotiqContentType]
  );

  const isFiltersAvailable = useMemo(() => {
    return filters.some((filter) => filter.filters.length > 0);
  }, [filters]);

  const selectedFiltersNumber = useMemo(
    () => Object.values(selectedFilters).filter(({ selected }) => selected).length,
    [selectedFilters]
  );

  const newFiltersNumber = useMemo(() => newFilters.length, [newFilters.length]);

  const handlefilterStores = useCallback(() => {
    if (filterByCartAvailabilityEnabled && !cartItems?.length) {
      setFilteredStores([]);
      return;
    }
    const storesFilteredBySelectedFilters = isFiltersAvailable
      ? getStoresFilteredBySelectedFilters(stores, selectedFilters, withinCategoryFilterOperator)
      : stores;

    const storesFilteredByProductAttributes = filterByCartAvailabilityEnabled
      ? getStoresFilteredByProductAttributes(
          storesFilteredBySelectedFilters,
          cartItems ?? [],
          categoryAttributeFilter ?? ""
        )
      : storesFilteredBySelectedFilters;
    if (storesFilteredBySelectedFilters.length > 0 || storesFilteredByProductAttributes.length > 0) {
      setIsNoStoresFilters(null);
    }

    if (storesFilteredBySelectedFilters.length && !storesFilteredByProductAttributes.length) {
      setIsNoMatchingFlotiqStores(true);
      setFilteredStores(storesFilteredBySelectedFilters);
    } else {
      setFilteredStores(storesFilteredByProductAttributes);
    }
  }, [
    stores,
    cartItems,
    filterByCartAvailabilityEnabled,
    isFiltersAvailable,
    selectedFilters,
    withinCategoryFilterOperator,
  ]);

  const handleLoadMarkersFromApi: GetFlotiqStores = useCallback(
    async (map) => {
      if (!map) {
        return;
      }

      if (activeStore) {
        setStores([activeStore]);
        return;
      }
      const mapData = getLatLngAndRadius(map);

      if (!mapData) {
        return;
      }

      const { lat, lng, radius, zoom } = mapData;

      try {
        const response = await flotiqApi.getStores({ radius, lat, lng, storesLimit });
        if (!response.data) {
          return;
        }
        setMapPosition({ lat, lng, zoom });

        const transformedMarkers = getTransformedMarkers(response.data, isAsianAddressFormatEnabled);
        const filtersBaseOnResult = getFiltersBaseOnFlotiqResponse(__, transformedMarkers, filtersTranslations ?? []);
        const newFiltersBaseOnResult = getNewFiltersBaseOnFlotiqResponse(
          __,
          transformedMarkers,
          filtersTranslations ?? [],
          enabledFiltersCategories
        );
        setStores(transformedMarkers);
        setFilters(filtersBaseOnResult);
        setNewFilters(newFiltersBaseOnResult);
      } catch (e) {
        console.error(e);
      }
    },
    [filtersTranslations, flotiqApi, storesLimit]
  );

  const debouncedLoadMarkersFromApi = useCallback(debounce(handleLoadMarkersFromApi, 300), [handleLoadMarkersFromApi]);

  const handleResetFilters = () => {
    setSelectedFilters({});
    setIsFiltersOpened(false);
    setIsNoStoresFilters(null);
  };

  const handleChangeFilters = (filters?: Record<string, StoreLocatorSelectedFilter>) => {
    if (!filters) {
      return;
    }
    setIsNoStoresFilters({ value: true, type: "filters" });
    setSelectedFilters(filters);
    setIsFiltersOpened(false);
  };

  const handleToggleFilters = () => {
    setIsFiltersOpened((opened) => !opened);
  };

  const handleLocationError = (browserHasGeolocation: boolean) => {
    const message = browserHasGeolocation
      ? __("storelocator.geolocation.error.failed")
      : __("storelocator.geolocation.error.browser.unsupported");
    toast.error(message);
  };

  const getUserMarkerPosition = () => {
    if (navigator?.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const pos = {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          };
          setUserMarkerPosition(pos);
        },
        () => {
          handleLocationError(true);
        }
      );
    } else {
      handleLocationError(false);
    }
  };

  const handleSelectStore = useCallback(
    (storeId: string) => {
      setActiveStoreId(storeId);
    },
    [setActiveStoreId]
  );

  useEffect(() => {
    setIsNoMatchingFlotiqStores(false);
    handlefilterStores();
  }, [handlefilterStores]);

  return {
    activeStoreId,
    userMarkerPosition,
    flotiqStores: filteredStores,
    isNoMatchingFlotiqStores,
    filters,
    newFilters,
    newFiltersNumber,
    selectedFilters,
    isFiltersOpened,
    isFiltersAvailable,
    selectedFiltersNumber,
    handleResetFilters,
    handleChangeFilters,
    handleToggleFilters,
    getUserMarkerPosition,
    getFlotiqStores: debouncedLoadMarkersFromApi,
    handleSelectStore,
    handleSetFlotiqStores: setStores,
    isNoStoresFilters,
  };
};
